import { Colors, Pagination, Spinner } from '@amelio/ui-library';

import ObjectiveCard from './ObjectiveCard';
import { EMPLOYEE_OBJECTIVES_EMPTY_TEXT, SECTION_TITLE_TIGHT } from './objectiveStyles';

import type { User } from '@/types/directory/User';
import type { ObjectiveOrDraft } from '@/types/objectives';
import type { ReactNode } from 'react';

import { useAmelioTranslations } from '@/hooks';

/** The list shows eight objectives, then paginates. */
const OBJECTIVES_PAGE_SIZE = 8;

interface EmployeeObjectivesListProps {
    objectives: ObjectiveOrDraft[];
    /** Resolves the owner and participant avatars the shared card draws. */
    employeesById: Map<string, User>;
    isLoading: boolean;
    /** 1-based. The tab owns it so it can send the reader back to page one on a period change. */
    page: number;
    onPageChange: (page: number) => void;
    /** Cards without their menu: a click opens the objective, and nothing else is offered. */
    isReadOnly?: boolean;
    /** Replaces the default "Objectives" title, for a list that introduces itself differently. */
    heading?: ReactNode;
    /** Replaces the default empty message, which speaks about the employee in the third person. */
    emptyMessage?: string;
    /** Set when the objectives could not be read: shown as an alert in place of the list. */
    errorMessage?: string;
    dataTestId?: string;
}

/**
 * The employee's objectives inside the selected period, one row each: pillar, title, a line of
 * description, the progress ring and the due date. The whole row is the link to the objective.
 */
export const EmployeeObjectivesList = ({
    objectives,
    employeesById,
    isLoading,
    page,
    onPageChange,
    isReadOnly = false,
    heading,
    emptyMessage,
    errorMessage,
    dataTestId = 'employee-objectives-list',
}: EmployeeObjectivesListProps) => {
    const { t } = useAmelioTranslations();

    const totalCount = objectives.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / OBJECTIVES_PAGE_SIZE));
    // Clamped rather than trusted: a shorter list must not leave the reader on a page that no
    // longer exists, showing nothing and offering no way back.
    const currentPage = Math.min(Math.max(1, page), totalPages);
    const firstOnPage = (currentPage - 1) * OBJECTIVES_PAGE_SIZE;
    const visibleObjectives = objectives.slice(firstOnPage, firstOnPage + OBJECTIVES_PAGE_SIZE);

    const renderBody = () => {
        if (errorMessage) {
            return (
                <p
                    role={'alert'}
                    className={'text-error py-6 text-center text-[14px]'}
                    data-testid={`${dataTestId}-error`}
                >
                    {errorMessage}
                </p>
            );
        }

        if (isLoading) {
            return (
                <div
                    className={'flex justify-center py-6'}
                    data-testid={`${dataTestId}-loading`}
                >
                    <Spinner
                        size={'md'}
                        color={Colors.PRIMARY}
                    />
                </div>
            );
        }

        if (objectives.length === 0) {
            return (
                <p
                    className={`${EMPLOYEE_OBJECTIVES_EMPTY_TEXT} py-6 text-center`}
                    data-testid={`${dataTestId}-empty`}
                >
                    {emptyMessage ?? t('performanceProfile.objectives.empty')}
                </p>
            );
        }

        return (
            <ul className={'flex flex-col gap-4'}>
                {visibleObjectives.map((objective) => (
                    <li
                        key={objective.id}
                        data-testid={`${dataTestId}-row-${objective.id}`}
                    >
                        {/* The objectives pages' own card, reused rather than redrawn, so an
                            objective looks the same wherever it is read; its menu offers only
                            what the reader may do, or nothing on a read-only list. */}
                        <ObjectiveCard
                            objective={objective}
                            employeesById={employeesById}
                            isReadOnly={isReadOnly}
                        />
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <section
            className={
                'flex flex-col gap-6 rounded-xl border border-white bg-white/45 px-4 py-4 md:px-8 md:py-6'
            }
            data-testid={dataTestId}
        >
            {heading ?? (
                <h3 className={SECTION_TITLE_TIGHT}>
                    {t('performanceProfile.objectives.listTitle')}
                </h3>
            )}

            {renderBody()}

            {/* Hidden below one full page, the same rule the Notes tab applies -- a pagination that
                says "page 1 of 1" is noise. `isLoading` is the library's own prop: while the list is
                on its way the control announces nothing, rather than a count nobody has checked. */}
            {!errorMessage && totalCount > OBJECTIVES_PAGE_SIZE && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    pageSize={OBJECTIVES_PAGE_SIZE}
                    onPageChange={onPageChange}
                    isLoading={isLoading}
                    resultsSummaryText={t('performanceProfile.objectives.pageSummary', {
                        start: firstOnPage + 1,
                        end: Math.min(firstOnPage + OBJECTIVES_PAGE_SIZE, totalCount),
                        total: totalCount,
                    })}
                    dataTestId={`${dataTestId}-pagination`}
                />
            )}
        </section>
    );
};
